import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row, Typography } from 'antd';

import { convertUTCToLocal, formatDate } from '@/utils/date';
import DatePicker from '@/views/components/Inputs/DatePicker';
import { CurrencySelect } from '@/views/screens/common';

import { USER_PREFERENCES } from '../../../../../../../../contants/common';
import useUserPreference from '../../../../../../../../redux/hooks/useUserPreference';
import { routeFileSelector } from '../../../../../../../../utilz/i18n';
import HiddenFormInput from '../../../../../../../components/Inputs/HiddenFormInput';
import OverChargeClaimReasonSelector from '../../../../../../../components/Selectors/OverChargeClaimReasonSelector';
import OverChargeClaimStatusSelector from '../../../../../../../components/Selectors/OverChargeClaimStatusSelector';
import OverChargeClaimTypeSelector from '../../../../../../../components/Selectors/OverChargeClaimTypeSelector';
import TMSFiledCard from '../../../../../../common/components/TMSFiledCard';

const OurClaimForm = ({ editMode = true, claimDetails, form }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const submittedDate = Form.useWatch('dateSubmitted', form);
  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT) || 'MM/DD/YYYY';

  const onChangeClaimStatus = (data) => {
    if (data?.value === 'C') {
      form.setFieldsValue({ closedDate: new Date().toISOString() });
    } else {
      form.setFieldsValue({ closedDate: undefined });
    }
  };
  return (
    <Row gutter={30}>
      <Col sm={6}>
        <CurrencySelect
          name="currencyCode"
          editMode={editMode}
          rules={[
            {
              message: t('translation:claims.pleaseSelectCurrency'),
              required: true,
            },
          ]}
        />
        <div className="mb-15">
          <TMSFiledCard title={t('translation:claims.ourClaim')}>{claimDetails?.customerClaimNo}</TMSFiledCard>
        </div>
        <div>
          <TMSFiledCard title={t('translation:claims.claimant')}>
            <span className="text-highlight fw-600">{claimDetails?.customerName}</span>
          </TMSFiledCard>
        </div>
        <div className="mb-10 mt-5">
          <TMSFiledCard title={t('translation:default.fields.claimantAddress')}>
            <Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>
              {String(claimDetails?.customerDisplayAddress ? claimDetails?.customerDisplayAddress : '').replace(
                '\r',
                '\n',
              )}
            </Typography.Paragraph>
          </TMSFiledCard>
        </div>
      </Col>
      <Col sm={6}>
        <OverChargeClaimStatusSelector
          editMode={editMode}
          label={t('translation:default.fields.status.label')}
          name="statusCode"
          rules={[
            {
              message: 'Please select a status',
              required: true,
            },
          ]}
          onChange={onChangeClaimStatus}
        />

        <OverChargeClaimReasonSelector
          name="reasonCode"
          margin="mb-0"
          label={t('translation:default.fields.reason.label')}
          editMode={editMode}
        />
      </Col>
      <Col sm={6}>
        <OverChargeClaimTypeSelector
          name="ocTypeKey"
          label={t('translation:default.fields.type')}
          editMode={editMode}
          rules={[
            {
              message: t('translation:default.documentManager.documentManager.message'),
              required: true,
            },
          ]}
        />
        <div className="mb-15">
          <TMSFiledCard title={t('translation:claims.overchargeSum')}>
            <span className="text-danger fw-800">
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={String(claimDetails?.overchargeSum ? claimDetails?.overchargeSum : 0)}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            </span>
          </TMSFiledCard>
        </div>
        <div className="mb-30 mt-5">
          <TMSFiledCard title={t('translation:default.fields.auditedBy')}>{claimDetails?.preparedBy}</TMSFiledCard>
          <HiddenFormInput name="auditedBy" />
        </div>
      </Col>
      <Col sm={6}>
        <div className="mb-30 mt-5">
          <TMSFiledCard title={t('translation:default.fields.preparedBy')}>{claimDetails?.preparedBy}</TMSFiledCard>
        </div>
        <div className="mb-15">
          <TMSFiledCard title={t('translation:default.fields.submittedBy')}>{claimDetails?.submittedBy}</TMSFiledCard>
        </div>
        {editMode ? (
          <DatePicker
            name="dateSubmitted"
            editMode={Boolean(editMode)}
            margin="m-0"
            label={t('translation:default.fields.submittedDate')}
            allowClear
          />
        ) : (
          <TMSFiledCard title={t('translation:default.fields.submittedDate')}>
            {submittedDate ? formatDate(convertUTCToLocal(submittedDate), userDateFormat) : ''}
          </TMSFiledCard>
        )}
        <HiddenFormInput name="dateSubmitted" />
      </Col>
    </Row>
  );
};

export default OurClaimForm;
